import { useCallback, useState } from "react";

import Tooltip from "components/ui/tooltip/Tooltip";
import { IconProps } from "../../../../lib/icons/untitled_ui/UIcon";
import { EventData, EventNames, useTracking } from "../../../../lib/tracking";
import { pluralise } from "../../../../lib/utils";
import Button, { ButtonType } from "../../../ui/button/Button";
import SaveListDrawer from "./SaveListDrawer";
import { ListEntities } from "./SaveToLists";

type Props = {
  /** One or more entities to save. The drawer saves them all in a single operation. */
  entityIds: string[];
  entityType: ListEntities;
  trackingData?: EventData;
  buttonProps?: {
    /** null renders an icon-only button, which takes its accessible name from the tooltip. */
    buttonText?: string | null;
    buttonType?: ButtonType;
    icon?: IconProps | null;
  };
  // Has to be used when button is in a table row, in a table. Otherwise it won't open
  onShowDrawer?: () => void;
};

export function SaveListButton({
  entityIds,
  entityType,
  buttonProps = {
    buttonType: "primary",
    // No `fill`, so the bookmark renders as an outline: this button is the action, not the
    // state. Callers that know the entity is saved pass `fill` to say so.
    icon: {
      icon: "bookmark",
      color: "sysTextWhite",
    },
  },
  trackingData,
  onShowDrawer,
}: Props) {
  const { logEvent } = useTracking();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleSaveClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      logEvent(EventNames.saveDataClicked, {
        ...trackingData,
        "Data type": entityType.toUpperCase(),
      });

      if (onShowDrawer) {
        onShowDrawer();
      } else {
        setIsDrawerOpen(true);
      }
    },
    [entityType, logEvent, onShowDrawer, trackingData],
  );

  const { buttonText, buttonType, icon } = buttonProps;
  // Square, so the icon sits in the middle of it rather than in a pill the width of its padding.
  const isIconOnly = buttonText === null;
  const label =
    entityIds.length > 1 ? `Save ${pluralise(entityIds.length, entityType)}` : `Save ${entityType}`;

  return (
    <>
      <Tooltip content={label}>
        <Button
          buttonType={buttonType}
          onClick={handleSaveClick}
          icon={icon?.icon}
          iconProps={{
            ...icon,
            fill: icon?.fill ?? "transparent",
          }}
          aria-label={label}
          width={isIconOnly ? 32 : undefined}
        >
          {isIconOnly ? null : buttonText ?? "Save"}
        </Button>
      </Tooltip>
      {!onShowDrawer && (
        <SaveListDrawer
          isOpen={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          entityType={entityType}
          entityIds={entityIds}
        />
      )}
    </>
  );
}
